"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { liveQuery } from 'dexie';
import { db } from '@/lib/localdb/db';
import SmartCardUI, { SmartCardRecipe } from './smartcard-ui';
import { AgentActionDeck } from './agent-action-deck';
import { agentSync, AgentMessage, asContent, cleanContent, parseSmartCard } from '@/lib/state/agent-sync';
import { loadLatestSession, persistMessageNonBlocking, generateSessionTitle } from '@/lib/services/agent-chat-store';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Send,
  Mic,
  Loader2,
  Volume2,
  VolumeX,
  CheckCircle2,
  XCircle,
  FileText,
  Plus,
  Image as ImageIcon,
  MapPin,
  Clock,
  Search,
  MoreVertical,
  Paperclip,
  X,
  Star,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { NotificationService } from '@/lib/notification-service';
import { compressImage } from '@/lib/image-utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useEquipment, useUnitOfWork } from '@/lib/data/provider';
import { useToast } from '@/hooks/use-toast';
import { useRouter } from 'next/navigation';
import { format, isToday, isYesterday } from 'date-fns';
import { cn, getFirstName } from '@/lib/utils';
import { bubbleClass, bubbleProseClass, GLASS_PANEL, GLASS_HEADER, GLASS_INPUT, GLASS_SECONDARY_BUBBLE } from '@/lib/ui-classes';
import Image from 'next/image';
import { useStore } from '@/lib/state/store';
import { executeHeatherAction } from '@/components/heather/heather-action-handler';
import { playChatTone, ToneSoundOption, ToneVolumeOption } from '@/lib/audio/chat-sound-effects';

function getDayLabel(timestamp: number) {
  const date = new Date(timestamp);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMMM do, yyyy');
}

function getUserAvatarSrc(name: string): string {
  const normalized = getFirstName(name || '').trim().toLowerCase();
  if (normalized.includes('93') || normalized.includes('lead') || normalized.includes('primary')) return '/baseline/truck-93.png';
  if (normalized.includes('91') || normalized.includes('secondary')) return '/baseline/truck-91.png';
  if (normalized === 'heather' || normalized === 'agent') return '/icons/heather/icon1.png';
  return '/baseline/truck-93.png';
}

/**
 * Deduplicates an array of messages by unique ID or sender+time+content signature.
 */
function dedupeMessages(msgs: AgentMessage[]): AgentMessage[] {
  const map = new Map<string, AgentMessage>();
  for (const m of msgs) {
    const key = m.id || `${m.sender}-${m.createdAt}-${typeof m.content === 'string' ? m.content : JSON.stringify(m.content)}`;
    map.set(key, m);
  }
  return Array.from(map.values()).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
}

/**
 * Core chat interface for interacting with Heather, team members, or hybrid channels.
 * Supports text, voice notes, attachments, GPS location sharing, audio tones, and Smart Cards.
 *
 * @param userId - ID of the current user.
 * @param className - Optional CSS classes for styling.
 * @param defaultChannel - The initial chat channel ('direct', 'team', or 'hybrid').
 */
export const AgentChat: React.FC<{
  userId: string;
  className?: string;
  defaultChannel?: AgentMessage['channel'];
  directTargetUserId?: string;
  visualStyle?: 'default' | 'heather-overlay';
}> = ({ userId, className, defaultChannel = 'direct', directTargetUserId, visualStyle = 'default' }) => {
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [attachments, setAttachments] = useState<{ type: 'image' | 'file' | 'audio'; url: string; file?: File; name: string; duration?: number }[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  const [isRecording, setIsRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [newMessageToneEnabled, setNewMessageToneEnabled] = useState(true);
  const [newMessageToneVolume, setNewMessageToneVolume] = useState<ToneVolumeOption>('medium');
  const [newMessageToneSound, setNewMessageToneSound] = useState<ToneSoundOption>('alert');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevMessageCount = useRef(0);
  const router = useRouter();
  const { toast } = useToast();
  const equipmentService = useEquipment();
  const uow = useUnitOfWork();

  const isPeerDirect = defaultChannel === 'direct' && !!directTargetUserId;
  const isHeatherOverlayStyle = visualStyle === 'heather-overlay';

  // Compute canonical channel key for starring/auto-popup
  const channelKey = isPeerDirect
    ? `direct:${directTargetUserId}`
    : (defaultChannel === 'direct' ? 'direct:heather' : (defaultChannel || 'team'));
  const fullStarKey = `${userId}:${channelKey}`;

  const starredChatChannels = useStore((s) => s.starredChatChannels);
  const toggleStarredChatChannel = useStore((s) => s.toggleStarredChatChannel);
  const triggerAttentionPulse = useStore((s) => s.triggerAttentionPulse);

  const isStarred = Boolean(
    starredChatChannels[channelKey] ||
    starredChatChannels[fullStarKey] ||
    (defaultChannel && starredChatChannels[defaultChannel])
  );

  const teamPresence = useStore((s) => s.teamPresence);
  const now = Date.now();
  const isPrimaryOnline = Boolean(teamPresence['Lead Applicator'] && (now - (teamPresence['Lead Applicator'] || 0) < 300000)) || userId.toLowerCase().includes('lead');
  const isSecondaryOnline = Boolean(teamPresence['Secondary Applicator'] && (now - (teamPresence['Secondary Applicator'] || 0) < 300000)) || userId.toLowerCase().includes('secondary');

  const channelTitle = isPeerDirect
    ? `Direct: ${getFirstName(directTargetUserId || '')}`
    : (defaultChannel === 'direct' ? 'Heather Comms' : (defaultChannel === 'team' ? 'Team Broadcast' : 'Team + Heather Radio'));
  const channelLabel = isPeerDirect
    ? 'Direct Encrypted'
    : (defaultChannel === 'direct' ? 'Private 1-on-1 with Heather' : (defaultChannel === 'team' ? 'Crew Only' : 'Crew + Heather • Speaks when spoken to'));

  // Play incoming tone safely
  const handlePlayIncomingTone = useCallback((tone?: ToneSoundOption, vol?: ToneVolumeOption) => {
    if (!newMessageToneEnabled) return;
    playChatTone(tone || newMessageToneSound, vol || newMessageToneVolume);
  }, [newMessageToneEnabled, newMessageToneSound, newMessageToneVolume]);

  // Focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 120);
    return () => clearTimeout(timer);
  }, [defaultChannel, directTargetUserId]);

  // Load sound preferences
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const savedEnabled = localStorage.getItem(`chat-new-message-tone-enabled-${userId}`);
    if (savedEnabled !== null) {
      setNewMessageToneEnabled(savedEnabled === 'true');
    }
    const savedVol = localStorage.getItem(`chat-new-message-tone-volume-${userId}`) as ToneVolumeOption | null;
    if (savedVol === 'low' || savedVol === 'medium' || savedVol === 'high') {
      setNewMessageToneVolume(savedVol);
    }
    const savedSound = localStorage.getItem(`chat-new-message-tone-sound-${userId}`) as ToneSoundOption | null;
    if (savedSound) {
      setNewMessageToneSound(savedSound);
    }
  }, [userId]);

  const updateSoundPref = (sound: ToneSoundOption) => {
    setNewMessageToneSound(sound);
    if (typeof window !== 'undefined') {
      localStorage.setItem(`chat-new-message-tone-sound-${userId}`, sound);
    }
    // Interactive audio preview
    playChatTone(sound, newMessageToneVolume);
  };

  const updateVolumePref = (vol: ToneVolumeOption) => {
    setNewMessageToneVolume(vol);
    if (typeof window !== 'undefined') {
      localStorage.setItem(`chat-new-message-tone-volume-${userId}`, vol);
    }
    // Interactive audio preview
    playChatTone(newMessageToneSound, vol);
  };

  const updateToneEnabledPref = (enabled: boolean) => {
    setNewMessageToneEnabled(enabled);
    if (typeof window !== 'undefined') {
      localStorage.setItem(`chat-new-message-tone-enabled-${userId}`, String(enabled));
    }
    if (enabled) {
      playChatTone(newMessageToneSound, newMessageToneVolume);
    }
  };

  // 1. Reactive Messages Loading (Firestore for Heather Direct, Dexie for Peer/Team)
  useEffect(() => {
    let mounted = true;

    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotificationPermission(Notification.permission);
    }

    // In-app Heather direct chat uses Firestore persistence (Dexie is bypassed)
    if (defaultChannel === 'direct' && !isPeerDirect) {
      void loadLatestSession(userId).then((result) => {
        if (!mounted) return;
        if (result) {
          setCurrentSessionId(result.session.id);
          setMessages(dedupeMessages(result.messages));
        } else {
          setCurrentSessionId(null);
          setMessages([]);
        }
      });
      return () => {
        mounted = false;
      };
    }

    if (!db) return;

    const subscription = liveQuery(async () => {
      const list = await agentSync.listMessages(
        userId,
        defaultChannel,
        undefined,
        isPeerDirect ? directTargetUserId : undefined
      );
      return dedupeMessages(list);
    }).subscribe({
      next: (msgs) => {
        if (!mounted) return;
        setMessages((prev) => {
          const prevMap = new Map(prev.map(m => [m.id, m]));
          const hasNew = msgs.some(m => m.id && !prevMap.has(m.id));
          if (hasNew || msgs.length !== prev.length) {
            if (msgs.length > prev.length) {
              const last = msgs[msgs.length - 1];
              if (last && last.sender !== userId && last.sender !== 'agent') {
                NotificationService.notifyMessage(last.sender, cleanContent(last.content));
              }
            }
            return dedupeMessages(msgs);
          }
          return prev;
        });
      },
      error: (err) => console.warn('[AgentChat] liveQuery error:', err),
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [userId, defaultChannel, isPeerDirect, directTargetUserId]);

  const handleNewChat = useCallback(() => {
    setCurrentSessionId(null);
    setMessages([]);
    toast({
      title: "New Conversation",
      description: "Started a fresh conversation session with Heather.",
    });
  }, [toast]);

  // 2. Smart Scroll & Sound Triggers
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });

      const lastMsg = messages[messages.length - 1];
      const isNew = prevMessageCount.current > 0 && messages.length > prevMessageCount.current;
      prevMessageCount.current = messages.length;

      if (lastMsg && lastMsg.sender !== userId && isNew && newMessageToneEnabled) {
        handlePlayIncomingTone();
      }

      if (lastMsg && lastMsg.sender === 'agent' && isNew && voiceEnabled) {
        speak(cleanContent(lastMsg.content));
      }
    } else {
      prevMessageCount.current = messages.length;
    }
  }, [messages, voiceEnabled, userId, newMessageToneEnabled, handlePlayIncomingTone]);

  // 3. Text-to-Speech Engine
  const speak = (text: string) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => v.name.includes("Google US English") || v.name.includes("Natural")) || voices.find(v => v.lang.startsWith('en'));
    if (preferred) utterance.voice = preferred;
    window.speechSynthesis.speak(utterance);
  };

  const handleSend = async (overrideContent?: string) => {
    const contentToSend = typeof overrideContent === 'string' ? overrideContent : input;
    if (!contentToSend.trim() && attachments.length === 0) return;
    setIsSending(true);

    const isHeatherDirect = !isPeerDirect && defaultChannel === 'direct';
    const mentionsHeather = /(^|\s)@?heather\b/i.test(contentToSend);
    // Team channel has NO Heather at all. Hybrid only responds when spoken to. Direct always responds.
    const shouldInvokeHeather = !isPeerDirect && (
      defaultChannel === 'direct' ||
      (defaultChannel === 'hybrid' && mentionsHeather)
    );

    if (shouldInvokeHeather) {
      setIsAgentTyping(true);
    }

    // For Heather direct chat, ensure session ID exists or start a fresh session
    let sessionId = currentSessionId;
    let isNewSession = false;
    if (isHeatherDirect) {
      if (!sessionId) {
        sessionId = crypto.randomUUID();
        setCurrentSessionId(sessionId);
        isNewSession = true;
      }
    }

    // 1. Construct User Message
    const nowTs = Date.now();
    const userMsg: AgentMessage = {
      id: crypto.randomUUID(),
      createdAt: nowTs,
      timestamp: nowTs,
      role: 'user',
      sender: userId,
      recipient: isPeerDirect ? directTargetUserId! : (defaultChannel === 'direct' ? 'agent' : (defaultChannel || 'team')),
      status: 'sent',
      content: contentToSend,
      channel: defaultChannel,
      sessionId: sessionId || undefined,
      attachments: attachments.map(a => ({ type: a.type, url: a.url, name: a.name }))
    };

    if (isHeatherDirect && sessionId) {
      // Non-blocking Firestore write-through (Dexie is bypassed)
      persistMessageNonBlocking(
        sessionId,
        userMsg,
        isNewSession ? { userId, title: generateSessionTitle(contentToSend) } : undefined
      );
    } else {
      // Add to DB for team/peer channels
      await agentSync.addMessage(userMsg);
    }

    setMessages(prev => dedupeMessages([...prev, userMsg]));
    setInput('');
    setAttachments([]);

    if (!shouldInvokeHeather) {
      setIsSending(false);
      setIsAgentTyping(false);
      return;
    }

    try {
      // 2. Invoke Heather Intelligence Endpoint (30s timeout)
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30_000);
      let res: Response;
      try {
        res = await fetch('/api/mcp-architect/invoke', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            input: { messages: [{ type: "human", content: userMsg.content }] },
            config: { configurable: { session_id: userId } }
          }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeout);
      }

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const data = await res.json();
      const allMessages = data.output?.messages || [];

      let lastAiMsg: any = null;
      for (let i = allMessages.length - 1; i >= 0; i--) {
        if (allMessages[i].type === 'ai') {
          lastAiMsg = allMessages[i];
          break;
        }
      }

      if (lastAiMsg) {
        const replyContent = lastAiMsg.content || "Operational acknowledgment received.";
        const replyTs = Date.now();

        const replyMsg: AgentMessage = {
          id: crypto.randomUUID(),
          createdAt: replyTs,
          timestamp: replyTs,
          role: 'agent',
          sender: 'agent',
          recipient: userId,
          status: 'received',
          content: replyContent,
          channel: defaultChannel,
          sessionId: sessionId || undefined,
        };

        if (isHeatherDirect && sessionId) {
          // Non-blocking Firestore write-through for agent reply
          persistMessageNonBlocking(sessionId, replyMsg);
          setMessages(prev => dedupeMessages([...prev, replyMsg]));
        } else {
          await agentSync.addMessage(replyMsg);
          const updatedList = await agentSync.listMessages(userId, defaultChannel, undefined, isPeerDirect ? directTargetUserId : undefined);
          setMessages(dedupeMessages(updatedList));
        }

        if (voiceEnabled) speak(cleanContent(replyContent));
      }
    } catch (error) {
      console.error("Heather Comms Error:", error);
      const isTimeout = error instanceof DOMException && error.name === 'AbortError';
      toast({
        title: isTimeout ? "Comms Timeout" : "Transmission Error",
        description: isTimeout
          ? "Heather did not respond in time. Operating on local telemetry."
          : "Failed to connect to Heather. Local logging active.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
      setIsAgentTyping(false);
    }
  };

  const handleShareLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: "GPS Unavailable", description: "Geolocation is not supported by your device.", variant: "destructive" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        const acc = Math.round(pos.coords.accuracy);
        const mapLink = `https://maps.google.com/?q=${lat},${lng}`;
        const text = `📍 **Field Location Shared**\n- Coordinates: \`${lat}, ${lng}\` (±${acc}m)\n- [Open in Google Maps](${mapLink})`;
        void handleSend(text);
        toast({ title: "GPS Broadcasted", description: `Coordinates ${lat}, ${lng} sent to channel.` });
      },
      (err) => {
        toast({ title: "GPS Fix Failed", description: err.message || "Unable to acquire location fix.", variant: "destructive" });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleClearHistory = async () => {
    try {
      if (!isPeerDirect && defaultChannel === 'direct') {
        handleNewChat();
        return;
      }
      const msgsToDelete = await agentSync.listMessages(userId, defaultChannel, undefined, isPeerDirect ? directTargetUserId : undefined);
      const idsToDelete = msgsToDelete.map((m) => m.id).filter((id): id is string => Boolean(id));
      if (idsToDelete.length > 0) {
        await db.messages.bulkDelete(idsToDelete);
      }
      setMessages([]);
      toast({ title: "History Cleared", description: "Channel conversation history has been cleared locally." });
    } catch (err) {
      console.error("Clear history error:", err);
      toast({ title: "Clear Failed", description: "Could not clear local message history.", variant: "destructive" });
    }
  };

  const handleReaction = async (msgId: string, emoji: string) => {
    if (!isPeerDirect && defaultChannel === 'direct') {
      setMessages(prev => prev.map(m => {
        if (m.id !== msgId) return m;
        const reactions = { ...(m.reactions || {}) };
        const users = new Set(reactions[emoji] || []);
        if (users.has(userId)) users.delete(userId);
        else users.add(userId);
        reactions[emoji] = Array.from(users);
        return { ...m, reactions };
      }));
      return;
    }
    await agentSync.addReaction(msgId, emoji, userId);
    const msgs = await agentSync.listMessages(userId, defaultChannel, undefined, isPeerDirect ? directTargetUserId : undefined);
    setMessages(dedupeMessages(msgs));
  };

  const processFile = async (file: File) => {
    let url = "";
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      const rawUrl = await new Promise<string>((resolve) => {
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      });
      url = await compressImage(rawUrl, { maxWidth: 1200, quality: 0.8 });
    } else {
      url = "file-icon-placeholder";
    }

    setAttachments(prev => [...prev, {
      type: file.type.startsWith('image/') ? 'image' : 'file',
      url,
      file,
      name: file.name
    }]);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => setIsDragging(false);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const toggleVoice = () => {
    if (!voiceEnabled) {
      window.speechSynthesis?.resume();
      toast({ title: "Voice Enabled", description: "Incoming Heather messages will be read aloud." });
    } else {
      window.speechSynthesis?.cancel();
    }
    setVoiceEnabled(!voiceEnabled);
  };

  // Voice Note Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = (e) => {
          const url = e.target?.result as string;
          setAttachments(prev => [...prev, {
            type: 'audio',
            url,
            name: `Voice Note - ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
            duration: recordTime
          }]);
        };
        reader.readAsDataURL(audioBlob);
        setRecordTime(0);
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordTime(0);
      recordIntervalRef.current = setInterval(() => {
        setRecordTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Recording failed:", err);
      toast({ title: "Microphone Inaccessible", description: "Microphone permission required for voice notes.", variant: "destructive" });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordIntervalRef.current) clearInterval(recordIntervalRef.current);
    }
  };

  // Mic Input Dictation
  const toggleMic = () => {
    if (isRecording) {
      stopRecording();
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast({ title: "Voice Dictation Unsupported", description: "Your browser does not support Web Speech Recognition.", variant: "destructive" });
      return;
    }
    if (isListening) {
      setIsListening(false);
    } else {
      setIsListening(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInput(prev => (prev ? `${prev} ${transcript}` : transcript));
        setIsListening(false);
      };
      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => setIsListening(false);
      recognition.start();
    }
  };

  const handleAction = async (msgId: string, actionId: string, values: any) => {
    await executeHeatherAction(msgId, actionId, values, {
      messages,
      equipmentService,
      router,
      currentUser: userId,
      toast,
      setMessages,
      uow
    });
  };

  const handleSnooze = async (msgId: string, until: number) => {
    await agentSync.snoozeMessage(msgId, until);
    const msgs = await agentSync.listMessages(userId, defaultChannel, undefined, isPeerDirect ? directTargetUserId : undefined);
    setMessages(dedupeMessages(msgs));
  };

  const activeCards = useMemo(() => messages.filter(m =>
    m.sender === 'agent' &&
    asContent(m.content)?.recipe &&
    !['snoozed', 'archived'].includes(m.status)
  ).map(m => ({ ...m, content: parseSmartCard(m.content) ?? m.content }))
    .filter(m => asContent(m.content)?.recipe), [messages]);

  return (
    <div
      className={`flex h-full w-full overflow-hidden relative ${className || ''} ${isDragging ? 'ring-4 ring-primary ring-inset' : ''}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/20 backdrop-blur-sm pointer-events-none border-4 border-dashed border-primary m-2 rounded-xl">
          <div className="bg-background/90 p-8 rounded-2xl shadow-2xl flex flex-col items-center gap-4 border border-primary/50 scale-110">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center animate-bounce">
              <Plus className="h-8 w-8 text-primary" />
            </div>
            <p className="text-xl font-black uppercase tracking-widest text-primary">Drop to Upload</p>
            <p className="text-sm font-medium opacity-70 italic text-center">Images will be compressed automatically</p>
          </div>
        </div>
      )}

      {/* LEFT: Chat Stream */}
      <div className="flex-1 flex flex-col min-w-0">
        <Card className={cn(
          "flex-1 flex flex-col overflow-hidden rounded-none border-0",
          isHeatherOverlayStyle
            ? `${GLASS_PANEL} text-white`
            : "bg-zinc-950 text-zinc-100"
        )}>
          {/* Header */}
          <CardHeader className={cn(
            "py-2.5 px-4 flex flex-row items-center justify-between shrink-0 z-10 sticky top-0 backdrop-blur-md border-b border-white/10",
            isHeatherOverlayStyle ? GLASS_HEADER : "bg-zinc-900/90"
          )}>
            <div className="flex items-center gap-3">
              {isSearching ? (
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-white/50" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search conversation..."
                    className="h-8 w-[160px] sm:w-[220px] bg-transparent border-none focus-visible:ring-0 p-0 text-sm text-white placeholder:text-white/40"
                    autoFocus
                  />
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-white/60 hover:text-white" onClick={() => { setIsSearching(false); setSearchQuery(''); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  {/* Dynamic Participant Avatars Cluster based on Active Channel */}
                  <div className="flex items-center -space-x-2 shrink-0">
                    {/* Peer Direct */}
                    {isPeerDirect && (
                      <div className="relative group" title={`${getFirstName(directTargetUserId || '')} — ${(directTargetUserId?.toLowerCase().includes('secondary') ? isSecondaryOnline : isPrimaryOnline) ? 'Online' : 'Offline'}`}>
                        <Avatar className="h-9 w-9 border-2 border-zinc-900 shadow-md">
                          <AvatarImage src={getUserAvatarSrc(directTargetUserId || '')} className="object-cover" />
                          <AvatarFallback className="bg-zinc-800 text-white font-bold text-xs">{getFirstName(directTargetUserId || '?').slice(0, 1).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span className={cn(
                          "absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-zinc-900",
                          (directTargetUserId?.toLowerCase().includes('secondary') ? isSecondaryOnline : isPrimaryOnline) ? "bg-emerald-500 animate-pulse" : "bg-zinc-600"
                        )} />
                      </div>
                    )}

                    {/* Team Channel (Crew Only, NO Heather) */}
                    {defaultChannel === 'team' && (
                      <>
                        <div className="relative group" title={`Lead Applicator — ${isPrimaryOnline ? 'Online' : 'Offline'}`}>
                          <Avatar className="h-8 w-8 border-2 border-zinc-900 shadow-sm">
                            <AvatarImage src={getUserAvatarSrc('lead')} className="object-cover" />
                            <AvatarFallback className="bg-blue-900 text-white font-bold text-[10px]">L</AvatarFallback>
                          </Avatar>
                          <span className={cn(
                            "absolute bottom-0 right-0 h-2 w-2 rounded-full border border-zinc-900",
                            isPrimaryOnline ? "bg-emerald-500 animate-pulse" : "bg-zinc-600"
                          )} />
                        </div>
                        <div className="relative group" title={`Secondary Applicator — ${isSecondaryOnline ? 'Online' : 'Offline'}`}>
                          <Avatar className="h-8 w-8 border-2 border-zinc-900 shadow-sm">
                            <AvatarImage src={getUserAvatarSrc('secondary')} className="object-cover" />
                            <AvatarFallback className="bg-emerald-900 text-white font-bold text-[10px]">S</AvatarFallback>
                          </Avatar>
                          <span className={cn(
                            "absolute bottom-0 right-0 h-2 w-2 rounded-full border border-zinc-900",
                            isSecondaryOnline ? "bg-emerald-500 animate-pulse" : "bg-zinc-600"
                          )} />
                        </div>
                      </>
                    )}

                    {/* Team + Heather Channel */}
                    {defaultChannel === 'hybrid' && (
                      <>
                        <div className="relative group" title={`Lead Applicator — ${isPrimaryOnline ? 'Online' : 'Offline'}`}>
                          <Avatar className="h-8 w-8 border-2 border-zinc-900 shadow-sm">
                            <AvatarImage src={getUserAvatarSrc('lead')} className="object-cover" />
                            <AvatarFallback className="bg-blue-900 text-white font-bold text-[10px]">L</AvatarFallback>
                          </Avatar>
                          <span className={cn(
                            "absolute bottom-0 right-0 h-2 w-2 rounded-full border border-zinc-900",
                            isPrimaryOnline ? "bg-emerald-500" : "bg-zinc-600"
                          )} />
                        </div>
                        <div className="relative group" title={`Secondary Applicator — ${isSecondaryOnline ? 'Online' : 'Offline'}`}>
                          <Avatar className="h-8 w-8 border-2 border-zinc-900 shadow-sm">
                            <AvatarImage src={getUserAvatarSrc('secondary')} className="object-cover" />
                            <AvatarFallback className="bg-emerald-900 text-white font-bold text-[10px]">S</AvatarFallback>
                          </Avatar>
                          <span className={cn(
                            "absolute bottom-0 right-0 h-2 w-2 rounded-full border border-zinc-900",
                            isSecondaryOnline ? "bg-emerald-500" : "bg-zinc-600"
                          )} />
                        </div>
                        <div className="relative group" title="Heather Intelligence — Active">
                          <Avatar className="h-8 w-8 border-2 border-zinc-900 shadow-sm">
                            <AvatarImage src="/icons/heather/icon1.png" className="object-cover" />
                            <AvatarFallback className="bg-cyan-900 text-white font-bold text-[10px]">H</AvatarFallback>
                          </Avatar>
                          <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-emerald-500 border border-zinc-900" />
                        </div>
                      </>
                    )}

                    {/* Direct Heather Channel */}
                    {defaultChannel === 'direct' && (
                      <>
                        <div className="relative group" title="Heather Intelligence — Active">
                          <Avatar className="h-8 w-8 border-2 border-zinc-900 shadow-sm">
                            <AvatarImage src="/icons/heather/icon1.png" className="object-cover" />
                            <AvatarFallback className="bg-cyan-900 text-white font-bold text-[10px]">H</AvatarFallback>
                          </Avatar>
                          <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-emerald-500 border border-zinc-900" />
                        </div>
                        <div className="relative group" title={`${userId} — Online`}>
                          <Avatar className="h-8 w-8 border-2 border-zinc-900 shadow-sm">
                            <AvatarImage src={getUserAvatarSrc(userId)} className="object-cover" />
                            <AvatarFallback className="bg-zinc-800 text-white font-bold text-[10px]">{userId.slice(0, 1)}</AvatarFallback>
                          </Avatar>
                          <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-emerald-500 border border-zinc-900" />
                        </div>
                      </>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center gap-1.5">
                      <h2 className="font-bold text-sm leading-tight text-white">
                        {channelTitle}
                      </h2>
                      {isStarred && (
                        <span className="flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                          <Star className="w-2.5 h-2.5 fill-amber-400" />
                          <span>AUTO-POPUP</span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] font-semibold text-cyan-400">
                        {channelLabel}
                      </span>
                      <span className="text-[10px] text-zinc-400">• Operational</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Star Auto-Popup Toggle Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  toggleStarredChatChannel(channelKey);
                  toggleStarredChatChannel(fullStarKey);
                  const nextState = !isStarred;
                  if (nextState) {
                    triggerAttentionPulse('amber');
                    playChatTone('confirmation', 'medium');
                    toast({
                      title: "Auto-Popup Starred",
                      description: `New messages in "${channelTitle}" will automatically pop up and pulse the screen.`,
                    });
                  } else {
                    toast({
                      title: "Auto-Popup Unstarred",
                      description: `Auto-popup disabled for "${channelTitle}".`,
                    });
                  }
                }}
                className={cn(
                  "h-8 w-8 p-0 rounded-full transition-all",
                  isStarred
                    ? "bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 hover:text-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.35)]"
                    : "text-white/40 hover:bg-white/10 hover:text-amber-400"
                )}
                title={isStarred ? "Auto-Popup on new messages: ENABLED (Starred)" : "Star this chat to auto-popup on new messages"}
              >
                <Star className={cn("h-4 w-4 transition-transform", isStarred && "fill-amber-400 scale-110")} />
              </Button>

              {/* Search Toggle */}
              {!isSearching && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsSearching(true)}
                  className="h-8 w-8 text-white/50 hover:bg-white/10 hover:text-white"
                  title="Search messages"
                >
                  <Search className="h-4 w-4" />
                </Button>
              )}

              {/* Voice TTS Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleVoice}
                className={`h-8 w-8 p-0 rounded-full transition-colors ${
                  voiceEnabled
                    ? 'bg-purple-900/40 text-purple-300 ring-1 ring-purple-500/50'
                    : 'text-white/50 hover:bg-white/10 hover:text-white'
                }`}
                title={voiceEnabled ? "Mute Voice Reading" : "Enable Voice Reading"}
              >
                {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>

              {/* New Conversation Session Button */}
              {!isPeerDirect && defaultChannel === 'direct' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNewChat}
                  className="h-8 w-8 text-white/50 hover:bg-white/10 hover:text-white"
                  title="Start new conversation"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}

              {/* Chat Settings & Notifications Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white/50 hover:bg-white/10 hover:text-white">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-zinc-950 border-white/15 text-zinc-100">
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-zinc-400 font-bold">
                    Comms & Notifications
                  </DropdownMenuLabel>
                  <DropdownMenuCheckboxItem
                    checked={isStarred}
                    onCheckedChange={() => {
                      toggleStarredChatChannel(channelKey);
                      toggleStarredChatChannel(fullStarKey);
                    }}
                  >
                    <span className="flex items-center gap-1.5 font-medium">
                      <Star className={cn("w-3.5 h-3.5", isStarred && "fill-amber-400 text-amber-400")} />
                      <span>Auto-Popup on Message</span>
                    </span>
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={newMessageToneEnabled}
                    onCheckedChange={(checked) => updateToneEnabledPref(Boolean(checked))}
                  >
                    Audio Tone on Message
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator className="bg-white/10" />

                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-zinc-400 font-bold">
                    Tone Sound (Click to Test)
                  </DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={newMessageToneSound} onValueChange={(val) => updateSoundPref(val as ToneSoundOption)}>
                    <DropdownMenuRadioItem value="alert">Alert Pulse</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="quick-chime">Quick Chime</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="bubble-pop">Bubble Pop</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="confirmation">Confirmation Ping</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="digital-quick">Digital Blip</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="electric-fence">Electric Industrial</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="message-pop">Message Pop</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="slot-win">Triumph Arpeggio</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="double">Double Chime</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="soft">Soft Ping</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>

                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-zinc-400 font-bold">
                    Tone Volume
                  </DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={newMessageToneVolume} onValueChange={(val) => updateVolumePref(val as ToneVolumeOption)}>
                    <DropdownMenuRadioItem value="low">Low (15%)</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="medium">Medium (45%)</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="high">High (90%)</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>

                  {!isPeerDirect && defaultChannel === 'direct' && (
                    <>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem
                        onClick={handleNewChat}
                        className="text-cyan-400 focus:bg-cyan-950 focus:text-cyan-300 flex items-center gap-2 cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Start New Session</span>
                      </DropdownMenuItem>
                    </>
                  )}

                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={handleClearHistory}
                    className="text-rose-400 focus:bg-rose-950 focus:text-rose-300 flex items-center gap-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Clear Local History</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>

          {/* Messages Stream */}
          <CardContent className="flex-1 p-0 overflow-hidden relative min-h-0 bg-black/40">
            <ScrollArea className="h-full w-full p-4" ref={scrollViewportRef}>
              <div className="space-y-5 pb-4">
                {(() => {
                  let lastDay = '';
                  const deduped = dedupeMessages(messages);
                  const filtered = deduped.filter(m => {
                    if (!searchQuery) return true;
                    const content = cleanContent(m.content).toLowerCase();
                    return content.includes(searchQuery.toLowerCase());
                  });

                  if (filtered.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-12 text-center text-zinc-500">
                        <div className="w-12 h-12 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center mb-3">
                          <Sparkles className="w-6 h-6 text-zinc-400" />
                        </div>
                        <p className="text-sm font-semibold text-zinc-300">No transmissions yet</p>
                        <p className="text-xs text-zinc-500 mt-1 max-w-xs">
                          {isPeerDirect
                            ? `Direct comms with ${getFirstName(directTargetUserId || 'Crew')}.`
                            : (defaultChannel === 'direct' ? 'Speak with Heather for telemetry, recipes, or dispatch.' : 'Send a message to the entire active crew.')}
                        </p>
                      </div>
                    );
                  }

                  return filtered.map((msg) => {
                    const isAgent = msg.sender === 'agent' || msg.sender === 'heather';
                    const rawSenderName = isAgent ? 'Heather' : (msg.sender === userId ? getFirstName(userId) : msg.sender);
                    const displaySenderName = getFirstName(rawSenderName);
                    const isUserMe = msg.sender === userId;
                    const msgDay = getDayLabel(msg.createdAt || Date.now());
                    const showDayHeader = msgDay !== lastDay;
                    lastDay = msgDay;

                    return (
                      <React.Fragment key={msg.id}>
                        {showDayHeader && (
                          <div className="flex justify-center my-3">
                            <span className="px-3 py-1 bg-zinc-900/90 border border-white/10 text-zinc-400 text-[10px] font-bold rounded-full uppercase tracking-wider">
                              {msgDay}
                            </span>
                          </div>
                        )}
                        <div className={`flex gap-3 group ${isUserMe ? 'flex-row-reverse' : ''}`}>
                          {/* Avatar */}
                          <Avatar className={`h-8 w-8 mt-1 flex-shrink-0 border shadow-sm ${
                            isAgent
                              ? 'border-cyan-500/50'
                              : (isUserMe ? 'border-blue-500/50' : 'border-emerald-500/50')
                          }`}>
                            <AvatarImage src={isAgent ? "/icons/heather/icon1.png" : getUserAvatarSrc(displaySenderName)} className="object-cover" />
                            <AvatarFallback className={`text-[10px] font-bold ${
                              isAgent ? 'bg-cyan-950 text-cyan-300' :
                              (isUserMe ? 'bg-blue-950 text-blue-300' : 'bg-emerald-950 text-emerald-300')
                            }`}>
                              {isAgent ? 'H' : (displaySenderName[0] || '?')}
                            </AvatarFallback>
                          </Avatar>

                          <div className={`flex flex-col max-w-[85%] ${isUserMe ? 'items-end' : 'items-start'}`}>
                            <span className="text-[10px] text-zinc-400 mb-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {displaySenderName} • {new Date(msg.createdAt || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>

                            {/* Message Bubble */}
                            <div className={bubbleClass(isUserMe, displaySenderName)}>
                              {msg.attachments && msg.attachments.length > 0 && (
                                <div className="flex flex-col gap-2 mb-2 w-full">
                                  {msg.attachments.map((att, i) => (
                                    <div key={i} className="relative rounded-lg overflow-hidden border border-white/10 shadow-inner bg-black/40">
                                      {att.type === 'image' ? (
                                        <Image src={att.url} alt="attachment" width={400} height={300} unoptimized className="object-cover max-h-60 w-full hover:scale-105 transition-transform duration-500 cursor-pointer" />
                                      ) : att.type === 'audio' ? (
                                        <div className="p-3 w-full min-w-[220px]">
                                          <div className="flex items-center gap-2 mb-2">
                                            <Volume2 className="h-4 w-4 text-cyan-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-300">Voice Recording</span>
                                          </div>
                                          <audio controls className="h-8 w-full opacity-90">
                                            <source src={att.url} type="audio/webm" />
                                          </audio>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-3 p-2.5 bg-zinc-900 hover:bg-zinc-800 transition-colors">
                                          <Paperclip className="h-4 w-4 text-zinc-400" />
                                          <span className="text-xs font-bold truncate text-zinc-200">{att.name || 'document'}</span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {(() => {
                                const parsed = parseSmartCard(msg.content);
                                if (parsed?.recipe) {
                                  return (
                                    <div className="flex flex-col gap-1.5">
                                      <div className="flex items-center gap-2 font-bold text-sm text-cyan-300">
                                        <FileText className="h-4 w-4 text-cyan-400" />
                                        <span>Action Card: {parsed.recipe.title}</span>
                                      </div>
                                      {parsed.recipe.description && (
                                        <p className="text-xs text-zinc-300">{parsed.recipe.description}</p>
                                      )}
                                      <div className="flex items-center gap-1.5 text-xs font-semibold mt-1">
                                        {msg.status === 'executing' && <Loader2 className="h-3.5 w-3.5 text-cyan-400 animate-spin" />}
                                        {msg.status === 'approved' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
                                        {msg.status === 'rejected' && <XCircle className="h-3.5 w-3.5 text-rose-400" />}
                                        {msg.status === 'conflict' && <Clock className="h-3.5 w-3.5 text-amber-400" />}
                                        <span className={
                                          msg.status === 'executing' ? "text-cyan-400 animate-pulse" :
                                          msg.status === 'approved' ? "text-emerald-400" :
                                          msg.status === 'rejected' ? "text-rose-400" :
                                          msg.status === 'conflict' ? "text-amber-400" : "text-zinc-400"
                                        }>
                                          {msg.status ? (msg.status.charAt(0).toUpperCase() + msg.status.slice(1)) : 'Pending Review'}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <div className={bubbleProseClass(isUserMe)}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {cleanContent(msg.content)}
                                    </ReactMarkdown>
                                  </div>
                                );
                              })()}

                              {/* Reactions */}
                              {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {Object.entries(msg.reactions).map(([emoji, users]) => (
                                    <button
                                      key={emoji}
                                      onClick={() => handleReaction(msg.id!, emoji)}
                                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border transition-colors ${
                                        users.includes(userId)
                                          ? 'bg-primary/20 border-primary/50 text-white'
                                          : 'bg-muted/50 border-white/10 text-muted-foreground hover:bg-muted'
                                      }`}
                                    >
                                      <span>{emoji}</span>
                                      <span>{users.length}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Quick Reaction Button */}
                            <div className={`mt-1 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity ${isUserMe ? 'flex-row-reverse' : ''}`}>
                              <button onClick={() => handleReaction(msg.id!, '👍')} className="text-xs hover:scale-125 transition-transform" title="Thumbs Up">👍</button>
                              <button onClick={() => handleReaction(msg.id!, '❤️')} className="text-xs hover:scale-125 transition-transform" title="Heart">❤️</button>
                              <button onClick={() => handleReaction(msg.id!, '✅')} className="text-xs hover:scale-125 transition-transform" title="Checkmark">✅</button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="text-xs hover:scale-125 transition-transform text-zinc-400 hover:text-white">➕</button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent side="top" className="flex gap-1 p-1 bg-zinc-950 border-white/20">
                                  {['🔥', '😮', '⚡', '💯', '🚀'].map(e => (
                                    <button key={e} onClick={() => handleReaction(msg.id!, e)} className="text-base hover:bg-zinc-800 p-1 rounded">{e}</button>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}

                {/* Typing Indicator */}
                {isAgentTyping && (
                  <div className="flex gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <Avatar className="h-8 w-8 mt-1 border border-cyan-500/30">
                      <AvatarImage src="/icons/heather/icon1.png" className="object-cover" />
                    </Avatar>
                    <div className="border border-cyan-500/30 rounded-2xl rounded-tl-sm px-4 py-2 flex items-center gap-2 bg-zinc-900/90 text-cyan-300 shadow-md">
                      <div className="flex gap-1.5">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" />
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Heather processing...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          </CardContent>

          {/* Footer Input Bar */}
          <CardFooter className={cn(
            "p-3 border-t shrink-0 gap-2 flex-col border-white/10",
            isHeatherOverlayStyle ? "bg-black/90" : "bg-zinc-900"
          )}>
            {/* Attachment Previews */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 w-full mb-1">
                {attachments.map((att, i) => (
                  <div key={i} className="relative group">
                    <div className="h-14 w-14 rounded border border-white/10 overflow-hidden bg-zinc-950 flex items-center justify-center">
                      {att.type === 'image' ? (
                        <Image src={att.url} alt="preview" layout="fill" objectFit="cover" unoptimized />
                      ) : att.type === 'audio' ? (
                        <Volume2 className="h-6 w-6 text-cyan-400 animate-pulse" />
                      ) : (
                        <Paperclip className="h-5 w-5 text-zinc-400" />
                      )}
                    </div>
                    <button
                      onClick={() => removeAttachment(i)}
                      className="absolute -top-1.5 -right-1.5 bg-rose-600 text-white rounded-full p-0.5 shadow-md hover:bg-rose-500 transition-colors z-10"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 w-full">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileUpload}
                accept="image/*,.pdf,.doc,.docx"
              />

              {/* + Action Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full text-zinc-400 hover:text-white hover:bg-white/10">
                    <Plus className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top" className="bg-zinc-950 border-white/15 text-zinc-100">
                  <DropdownMenuItem onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-emerald-400" />
                    <span>Upload Photo</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-cyan-400" />
                    <span>Attach Document</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleShareLocation} className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-indigo-400" />
                    <span>Broadcast GPS Location</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={startRecording} className="flex items-center gap-2">
                    <Mic className="h-4 w-4 text-amber-400" />
                    <span>Record Voice Note</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Text Input / Recording State */}
              <div className="flex-1 relative">
                {isRecording ? (
                  <div className="flex items-center justify-between w-full h-10 px-4 bg-rose-950/40 border border-rose-500/40 rounded-full animate-pulse">
                    <div className="flex items-center gap-2.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping" />
                      <span className="text-xs font-black uppercase tracking-widest text-rose-400">Recording Voice Note</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm text-rose-300 font-bold">
                        {Math.floor(recordTime / 60)}:{(recordTime % 60).toString().padStart(2, '0')}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={stopRecording}
                        className="h-6 px-2 text-[10px] font-bold text-emerald-400 hover:bg-emerald-950"
                      >
                        DONE
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Input
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          void handleSend();
                        }
                      }}
                      placeholder={isPeerDirect
                        ? `Message ${getFirstName(directTargetUserId || 'Operator')}...`
                        : (defaultChannel === 'direct' ? "Message Heather..." : `Message ${defaultChannel === 'team' ? 'All Crew' : 'Group'}...`)}
                      className={cn(
                        "w-full rounded-full pl-4 pr-10 border-white/20 focus:border-cyan-400 focus:ring-cyan-400/30 transition-all h-10 text-white placeholder:text-zinc-500",
                        isHeatherOverlayStyle ? GLASS_INPUT : "bg-zinc-950"
                      )}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1 h-8 w-8 rounded-full text-zinc-400 hover:text-white"
                      onClick={toggleMic}
                      title={isListening ? "Listening..." : "Dictate via Voice"}
                    >
                      <Mic className={`h-4 w-4 ${isListening ? 'text-rose-500 animate-pulse' : ''}`} />
                    </Button>
                  </>
                )}
              </div>

              {/* Send Button */}
              <Button
                onClick={() => (isRecording ? stopRecording() : handleSend())}
                disabled={isSending || (!input.trim() && attachments.length === 0 && !isRecording)}
                size="icon"
                className={`h-10 w-10 rounded-full flex-shrink-0 shadow-lg transition-all ${
                  (input.trim() || attachments.length > 0 || isRecording)
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                    : 'bg-zinc-800 text-zinc-500'
                }`}
              >
                {isSending ? (
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                ) : isRecording ? (
                  <div className="h-3.5 w-3.5 bg-white rounded-sm" />
                ) : (
                  <Send className="h-4 w-4 text-white ml-0.5" />
                )}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>

      {/* RIGHT: Action Deck */}
      {activeCards.length > 0 && (
        <div className="w-[380px] shrink-0 animate-in slide-in-from-right duration-300 border-l border-white/10 bg-zinc-950 shadow-2xl z-10">
          <AgentActionDeck
            messages={activeCards}
            onAction={handleAction}
            onSnooze={handleSnooze}
          />
        </div>
      )}
    </div>
  );
};

export default AgentChat;
